<template>
  <header
    id="PageHeader"
    class="page-header h-header-bar-height-mobile lg:h-header-bar-height sticky top-0 z-40 w-full bg-white border-b flex justify-between"
  >
    <button
      class="lg:hidden w-header-bar-height-mobile justify-self-start flex items-center justify-center h-full px-2"
      @click="handleHamburgerClick()"
      aria-label="Open menu"
      aria-expanded="false"
      aria-controls="mainNavigation"
    >
      <IconBurger />
    </button>

    <div class="justify-self-center lg:justify-self-start">
      <nuxt-link
        :to="$localeRootPath('/')"
        class="hover:text-black hover:no-underline lg:pl-8 w-32 sm:w-44 md:w-48 lg:w-60 flex items-center h-full px-4 text-black no-underline ring-inset focus-visible:focus-ring-inside"
        :aria-label="$t('navigation.aria.home')"
        :title="$t('common.backToHome')"
      >
        <Logo />
      </nuxt-link>
    </div>

    <MainNav id="mainNavigation" class="col-grow w-full" />

    <div class="flex items-center justify-end" role="group" :aria-label="$t('navigation.aria.userTools')">
      <SearchInput />
      <RegionAndLanguageIndicator class="ml-1" />
      <PopoverHelp />
      <PopoverAuth />
      <CartButton />
    </div>
  </header>
</template>

<script>
import CartButton from '@/components/CartButton';
import IconBurger from '@/components/layout/header/IconBurger';
import Logo from '@/components/layout/header/Logo';
import MainNav from '@/components/layout/header/MainNav';
import PopoverAuth from '@/components/layout/header/PopoverAuth';
import PopoverHelp from '@/components/layout/header/PopoverHelp';
import RegionAndLanguageIndicator from '@/components/layout/header/RegionAndLanguageIndicator';
import SearchInput from '@/components/layout/header/SearchInput';
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useMainNavStore } from '@/stores/main-nav';
import { useSearchStore } from '@/stores/search';
import { useI18n } from 'vue-i18n';

export default defineNuxtComponent({
  components: {
    CartButton,
    IconBurger,
    Logo,
    MainNav,
    PopoverAuth,
    PopoverHelp,
    RegionAndLanguageIndicator,
    SearchInput,
  },
  setup() {
    const mainNavStore = useMainNavStore();
    const searchStore = useSearchStore();
    const route = useRoute();
    const { locale, t } = useI18n();
    const config = useRuntimeConfig();

    const isAccountPopoverOpen = ref(false);
    const isHelpPopoverOpen = ref(false);
    const isLangPopoverOpen = ref(false);
    const currentlyOpenPopover = ref(null); // "LOCALES", "HELP", "ACCOUNT"

    const activeNavItemIndex = computed(() => mainNavStore.activeNavItemIndex);
    const hasAlternates = computed(() => mainNavStore.hasAlternates);

    const currentLocale = computed(() => locale.value);
    const accountEnabled = computed(() => config.public.auth.isEnabled);

    function handleHamburgerClick() {
      mainNavStore.open();
    }

    function toggleCurrentlyOpenPopover(popoverName) {
      currentlyOpenPopover.value = currentlyOpenPopover.value === popoverName ? null : popoverName;
    }

    function closeAllPopovers() {
      currentlyOpenPopover.value = null;
    }

    function handleScroll() {
      closeAllPopovers();
    }

    function openMobileSearch() {
      searchStore.open();
    }

    watch(
      () => route.fullPath,
      () => {
        closeAllPopovers();
      }
    );

    watch(activeNavItemIndex, () => {
      closeAllPopovers();
    });

    onMounted(() => {
      window.addEventListener('scroll', handleScroll, { passive: true });
    });

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', handleScroll);
    });

    return {
      isAccountPopoverOpen,
      isHelpPopoverOpen,
      isLangPopoverOpen,
      currentlyOpenPopover,
      activeNavItemIndex,
      hasAlternates,
      currentLocale,
      accountEnabled,
      handleHamburgerClick,
      toggleCurrentlyOpenPopover,
      closeAllPopovers,
      handleScroll,
      openMobileSearch,
      t,
    };
  },
});
</script>
